import React, { useEffect, useRef } from 'react';
import { SimplexNoise, MeshGradient, ImageDithering, DotOrbit } from '@paper-design/shaders-react';
import 'sakura-js/dist/sakura.min.css';
import sakuraScriptUrl from 'sakura-js/dist/sakura.min.js?url';

const LazyGhibliBackground = React.lazy(() => import('../components/GhibliBackground'));
const LazyMedusaeBackground = React.lazy(() => import('../components/MedusaeBackground'));
const LazyVantaWavesBackground = React.lazy(() => import('../components/VantaWavesBackground'));
const LazyVantaTopologyBackground = React.lazy(() => import('../components/VantaTopologyBackground'));
const LazyLiquidGradientBackground = React.lazy(() => import('../components/LiquidGradientBackground'));

// ============================================
// THEME TOKEN TYPES
// ============================================
export interface ThemeTokens {
  background: {
    overlay: string; // Scrim overlay for readability
    overlayStrength: number; // 0-1 opacity
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
    accent: string;
  };
  panel: {
    bg: string;
    border: string;
    shadow: string;
  };
  button: {
    primary: {
      bg: string;
      text: string;
      hover: string;
    };
    secondary: {
      bg: string;
      text: string;
      hover: string;
    };
  };
  fonts: {
    heading: string;
    body: string;
    mono: string;
  };
  radius: {
    sm: string;
    md: string;
    lg: string;
  };
  // Visual mood
  mood: 'light' | 'dark' | 'colorful';
}

// ============================================
// PRESET TYPE
// ============================================
export interface ShaderPreset {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>;
  theme: ThemeTokens;
}

// ============================================
// STYLE TYPE
// ============================================
export interface ShaderStyle {
  id: string;
  name: string;
  presets: ShaderPreset[];
  renderShader: (props: { width: number; height: number; presetProps: Record<string, any> }) => React.ReactNode;
}

// ============================================
// THEME TOKEN PRESETS
// ============================================

// Dark theme base (for colorful/dark backgrounds)
const darkThemeBase: ThemeTokens = {
  background: {
    overlay: 'rgba(0, 0, 0, 0.6)',
    overlayStrength: 0.85, // Increased from 0.6 for better text readability
  },
  text: {
    primary: '#ffffff',
    secondary: '#e4e4e7',
    muted: '#a1a1aa',
    accent: '#ffffff',
  },
  panel: {
    bg: 'rgba(0, 0, 0, 0.4)',
    border: 'rgba(255, 255, 255, 0.1)',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  button: {
    primary: {
      bg: '#ffffff',
      text: '#0a0a0a',
      hover: '#e4e4e7',
    },
    secondary: {
      bg: 'rgba(255, 255, 255, 0.1)',
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.2)',
    },
  },
  fonts: {
    heading: "'Geist', system-ui, sans-serif",
    body: "'Geist', system-ui, sans-serif",
    mono: "'Geist Mono', 'SF Mono', Monaco, Consolas, monospace",
  },
  radius: {
    sm: '2px',
    md: '4px',
    lg: '8px',
  },
  mood: 'dark',
};

// Light theme base (for light backgrounds)
const lightThemeBase: ThemeTokens = {
  background: {
    overlay: 'rgba(255, 255, 255, 0.3)',
    overlayStrength: 0.3,
  },
  text: {
    primary: '#0a0a0a',
    secondary: '#27272a',
    muted: '#52525b',
    accent: '#0a0a0a',
  },
  panel: {
    bg: 'rgba(255, 255, 255, 0.6)',
    border: 'rgba(0, 0, 0, 0.1)',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  button: {
    primary: {
      bg: '#0a0a0a',
      text: '#ffffff',
      hover: '#27272a',
    },
    secondary: {
      bg: 'rgba(0, 0, 0, 0.05)',
      text: '#0a0a0a',
      hover: 'rgba(0, 0, 0, 0.1)',
    },
  },
  fonts: {
    heading: "'Geist', system-ui, sans-serif",
    body: "'Geist', system-ui, sans-serif",
    mono: "'Geist Mono', 'SF Mono', Monaco, Consolas, monospace",
  },
  radius: {
    sm: '4px',
    md: '8px',
    lg: '16px',
  },
  mood: 'light',
};

// Colorful theme variant
const colorfulTheme = (accentColor: string): ThemeTokens => ({
  ...darkThemeBase,
  text: {
    ...darkThemeBase.text,
    accent: accentColor,
  },
  button: {
    primary: {
      bg: accentColor,
      text: '#ffffff',
      hover: accentColor,
    },
    secondary: darkThemeBase.button.secondary,
  },
  mood: 'colorful',
});

// Geist Pixel first-class theme (white base + pixel atmosphere)
const geistPixelTheme: ThemeTokens = {
  background: {
    overlay: 'rgba(255, 255, 255, 0.12)',
    overlayStrength: 0.14,
  },
  text: {
    primary: '#0a0a0a',
    secondary: '#30303a',
    muted: '#616170',
    accent: '#111827',
  },
  panel: {
    bg: 'rgba(255, 255, 255, 0.82)',
    border: 'rgba(0, 0, 0, 0.32)',
    shadow: '0 14px 40px rgba(17, 24, 39, 0.08)',
  },
  button: {
    primary: {
      bg: '#111827',
      text: '#ffffff',
      hover: '#0f172a',
    },
    secondary: {
      bg: 'rgba(17, 24, 39, 0.06)',
      text: '#111827',
      hover: 'rgba(17, 24, 39, 0.12)',
    },
  },
  fonts: {
    heading: "'Geist', system-ui, sans-serif",
    body: "'Geist', system-ui, sans-serif",
    mono: "'Geist Mono', 'SF Mono', Monaco, Consolas, monospace",
  },
  radius: {
    sm: '4px',
    md: '8px',
    lg: '14px',
  },
  mood: 'light',
};

declare global {
  interface Window {
    Sakura?: new (
      selector: string,
      options?: {
        className?: string;
        fallSpeed?: number;
        maxSize?: number;
        minSize?: number;
        delay?: number;
        colors?: Array<{
          gradientColorStart: string;
          gradientColorEnd: string;
          gradientColorDegree: number;
        }>;
      }
    ) => { stop: (graceful?: boolean) => void };
  }
}

const SakuraBackground: React.FC<{ width: number; height: number }> = ({ width, height }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const sakuraInstanceRef = useRef<{ stop: (graceful?: boolean) => void } | null>(null);
  const classRef = useRef(`sakura-host-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    let cancelled = false;
    let scriptEl: HTMLScriptElement | null = null;

    const start = () => {
      if (cancelled || !hostRef.current || !window.Sakura) return;
      if (sakuraInstanceRef.current) {
        sakuraInstanceRef.current.stop(true);
        sakuraInstanceRef.current = null;
      }

      sakuraInstanceRef.current = new window.Sakura(`.${classRef.current}`, {
        className: 'sakura',
        fallSpeed: 1.92,
        minSize: 8,
        maxSize: 14,
        delay: 226,
        colors: [
          {
            gradientColorStart: 'rgba(255, 201, 214, 0.84)',
            gradientColorEnd: 'rgba(243, 160, 186, 0.82)',
            gradientColorDegree: 120,
          },
          {
            gradientColorStart: 'rgba(255, 221, 230, 0.78)',
            gradientColorEnd: 'rgba(236, 176, 198, 0.78)',
            gradientColorDegree: 120,
          },
        ],
      });
    };

    if (window.Sakura) {
      start();
    } else {
      scriptEl = document.createElement('script');
      scriptEl.src = sakuraScriptUrl;
      scriptEl.async = true;
      scriptEl.onload = start;
      document.body.appendChild(scriptEl);
    }

    return () => {
      cancelled = true;
      if (sakuraInstanceRef.current) {
        sakuraInstanceRef.current.stop(true);
        sakuraInstanceRef.current = null;
      }
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className={classRef.current}
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    />
  );
};

const geistPixelStyle: ShaderStyle = {
  id: 'geist-pixel',
  name: 'Geist Pixel',
  presets: [
    {
      id: 'geist-pixel-1',
      name: 'Petalos de Sakura',
      props: { variant: 'sakura' },
      theme: geistPixelTheme,
    },
    {
      id: 'geist-pixel-3',
      name: 'Ghibli',
      props: { variant: 'ghibli' },
      theme: {
        ...geistPixelTheme,
        background: {
          ...geistPixelTheme.background,
          overlayStrength: 0.2,
        },
        panel: {
          ...geistPixelTheme.panel,
          bg: 'rgba(255, 255, 255, 0.9)',
          border: 'rgba(0, 0, 0, 0.36)',
        },
      },
    },
    {
      id: 'geist-pixel-4',
      name: 'Breath Dear Medusae',
      props: { variant: 'medusae' },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0d1017',
          secondary: '#1f2b43',
          muted: '#3f4d68',
          accent: '#2f65ff',
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.72)',
          border: 'rgba(13, 16, 23, 0.2)',
          shadow: '0 18px 44px rgba(38, 73, 157, 0.12)',
        },
      },
    },
    {
      id: 'geist-pixel-5',
      name: 'Vanta Waves',
      props: { variant: 'vanta-waves' },
      theme: {
        ...lightThemeBase,
        background: {
          overlay: 'rgba(255, 255, 255, 0.38)',
          overlayStrength: 0.38,
        },
        text: {
          primary: '#0b1220',
          secondary: '#22324f',
          muted: '#4a5d80',
          accent: '#4259ff',
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.84)',
          border: 'rgba(10, 20, 40, 0.26)',
          shadow: '0 18px 46px rgba(23, 38, 80, 0.18)',
        },
      },
    },
  ],
  renderShader: ({ width, height, presetProps }) => {
    const variant = presetProps?.variant as string | undefined;
    if (variant === 'ghibli' || variant === 'ghibli-optimized') {
      return (
        <React.Suspense fallback={<SakuraBackground width={width} height={height} />}>
          <LazyGhibliBackground width={width} height={height} quality="optimized" />
        </React.Suspense>
      );
    }
    if (variant === 'medusae') {
      return (
        <React.Suspense fallback={<SakuraBackground width={width} height={height} />}>
          <LazyMedusaeBackground width={width} height={height} />
        </React.Suspense>
      );
    }
    if (variant === 'vanta-waves') {
      return (
        <React.Suspense fallback={<SakuraBackground width={width} height={height} />}>
          <LazyVantaWavesBackground width={width} height={height} />
        </React.Suspense>
      );
    }
    return <SakuraBackground width={width} height={height} />;
  },
};

// ============================================
// SIMPLEX NOISE STYLE (4 presets)
// ============================================
const simplexNoiseStyle: ShaderStyle = {
  id: 'simplex-noise',
  name: 'SimplexNoise',
  presets: [
    {
      id: 'simplex-1',
      name: 'Vibrant',
      props: {
        colors: ["#4449cf", "#ffd1e0", "#f94346", "#ffd36b", "#ffffff"],
        stepsPerColor: 2,
        softness: 0,
        speed: 0.5,
        scale: 0.6,
      },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0A0A0A',      // Negro para contraste en fondo claro
          secondary: '#27272A',    // Gris oscuro
          muted: '#52525B',        // Gris medio
          accent: '#f94346',       // Rojo vibrante para acentos
        },
        button: {
          primary: {
            bg: '#f94346',
            text: '#ffffff',
            hover: '#e03030',
          },
          secondary: {
            bg: 'rgba(0, 0, 0, 0.05)',
            text: '#0A0A0A',
            hover: 'rgba(0, 0, 0, 0.1)',
          },
        },
        mood: 'light',
      },
    },
    {
      id: 'simplex-2',
      name: 'Sunset',
      props: {
        colors: ["#ff7b00", "#f9ffeb", "#320d82"],
        stepsPerColor: 1,
        softness: 0,
        speed: 0.6,
      },
      theme: colorfulTheme('#ff7b00'),
    },
    {
      id: 'simplex-3',
      name: 'Ethereal',
      props: {
        colors: ["#e7cae5", "#110d21", "#442c44", "#e7bcbc", "#fff5f5"],
        stepsPerColor: 2,
        softness: 0,
        speed: 2,
        scale: 0.2,
      },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0A0A0A',      // Negro para contraste en fondo claro
          secondary: '#27272A',    // Gris oscuro
          muted: '#52525B',        // Gris medio
          accent: '#e7cae5',       // Rosa etéreo para acentos
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.7)',
          border: 'rgba(0, 0, 0, 0.1)',
          shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        },
        button: {
          primary: {
            bg: '#e7cae5',
            text: '#0A0A0A',
            hover: '#d4b0d1',
          },
          secondary: {
            bg: 'rgba(0, 0, 0, 0.05)',
            text: '#0A0A0A',
            hover: 'rgba(0, 0, 0, 0.1)',
          },
        },
        mood: 'light',
      },
    },
    {
      id: 'simplex-4',
      name: 'Soft Glow',
      props: {
        colors: ["#ffffff", "#ff9e9e", "#5f57ff", "#00f7ff"],
        stepsPerColor: 1,
        softness: 1,
        speed: 2,
        scale: 1.6,
      },
      theme: {
        ...lightThemeBase,
        text: { ...lightThemeBase.text, accent: '#5f57ff' },
        button: {
          primary: { bg: '#5f57ff', text: '#ffffff', hover: '#4a43e6' },
          secondary: lightThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'simplex-5',
      name: 'Pink Dream',
      props: {
        colors: ["#FFB6C1", "#FFC0CB", "#FFE4E1", "#F8D7DA", "#FF69B4"],
        stepsPerColor: 2,
        softness: 0.5,
        speed: 0.8,
        scale: 0.8,
      },
      theme: {
        // Fondo rosado claro → Texto oscuro (aplicando teoría del color)
        background: {
          overlay: 'rgba(255, 182, 193, 0.2)', // Rosa claro con poca opacidad
          overlayStrength: 0.2,
        },
        text: {
          primary: '#0A0A0A',      // Negro - máximo contraste sobre rosa claro
          secondary: '#27272A',    // Gris oscuro - buen contraste
          muted: '#52525B',        // Gris medio - para texto secundario
          accent: '#DC143C',       // Rosa intenso para acentos
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.7)', // Panel blanco semi-transparente
          border: 'rgba(0, 0, 0, 0.1)',
          shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        },
        button: {
          primary: {
            bg: '#DC143C',         // Rosa intenso
            text: '#FFFFFF',       // Blanco sobre botón rosa
            hover: '#C0112F',
          },
          secondary: {
            bg: 'rgba(0, 0, 0, 0.05)',
            text: '#0A0A0A',       // Negro sobre fondo claro
            hover: 'rgba(0, 0, 0, 0.1)',
          },
        },
        fonts: {
          heading: "'Inter', system-ui, sans-serif",
          body: "'Inter', system-ui, sans-serif",
          mono: "'SF Mono', Monaco, Consolas, monospace",
        },
        radius: {
          sm: '4px',
          md: '8px',
          lg: '16px',
        },
        mood: 'light',
      },
    },
  ],
  renderShader: ({ width, height, presetProps }) => (
    <SimplexNoise width={width} height={height} {...presetProps} />
  ),
};

// ============================================
// TOPOLOGY STYLE (3 presets)
// ============================================
const topologyStyle: ShaderStyle = {
  id: 'topology',
  name: 'Topology',
  presets: [
    {
      id: 'topology-1',
      name: 'Topology Classic',
      props: {
        options: {
          color: 0x8999ce,
          backgroundColor: 0x101623,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200,
          minWidth: 200,
          scale: 1,
          scaleMobile: 1,
        },
      },
      theme: {
        ...darkThemeBase,
        panel: {
          bg: 'rgba(12, 16, 28, 0.62)',
          border: 'rgba(255, 255, 255, 0.16)',
          shadow: '0 16px 40px rgba(0, 0, 0, 0.38)',
        },
      },
    },
    {
      id: 'topology-2',
      name: 'Topology Mint',
      props: {
        options: {
          color: 0x69d7c6,
          backgroundColor: 0x071a1d,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200,
          minWidth: 200,
          scale: 1,
          scaleMobile: 1,
        },
      },
      theme: {
        ...darkThemeBase,
        text: {
          ...darkThemeBase.text,
          accent: '#69d7c6',
        },
        panel: {
          bg: 'rgba(7, 26, 29, 0.58)',
          border: 'rgba(105, 215, 198, 0.26)',
          shadow: '0 18px 46px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    {
      id: 'topology-3',
      name: 'Topology Soft Light',
      props: {
        options: {
          color: 0x5b6cff,
          backgroundColor: 0xf4f7ff,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200,
          minWidth: 200,
          scale: 1,
          scaleMobile: 1,
        },
      },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0d1324',
          secondary: '#26324d',
          muted: '#4c5b7c',
          accent: '#5b6cff',
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.72)',
          border: 'rgba(9, 20, 48, 0.2)',
          shadow: '0 16px 38px rgba(18, 37, 88, 0.12)',
        },
      },
    },
    {
      id: 'topology-4',
      name: 'Liquid Gradient',
      props: {
        variant: 'liquid-gradient',
      },
      theme: {
        ...darkThemeBase,
        text: {
          primary: '#ffffff',
          secondary: '#d8e7df',
          muted: '#9fbcb0',
          accent: '#F15A22',
        },
        panel: {
          bg: 'rgba(0, 0, 0, 0.52)',
          border: 'rgba(241, 90, 34, 0.3)',
          shadow: '0 20px 52px rgba(0, 0, 0, 0.45)',
        },
      },
    },
  ],
  renderShader: ({ width, height, presetProps }) => {
    const variant = presetProps?.variant as string | undefined;
    if (variant === 'liquid-gradient') {
      return (
        <React.Suspense fallback={<div className="w-full h-full bg-[#000000]" />}>
          <LazyLiquidGradientBackground width={width} height={height} />
        </React.Suspense>
      );
    }
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#0f172a]" />}>
        <LazyVantaTopologyBackground width={width} height={height} options={presetProps?.options} />
      </React.Suspense>
    );
  },
};

// ============================================
// MESH GRADIENT STYLE (4 presets)
// ============================================
const meshGradientStyle: ShaderStyle = {
  id: 'mesh-gradient',
  name: 'MeshGradient',
  presets: [
    {
      id: 'mesh-1',
      name: 'Aurora',
      props: {
        colors: ["#e0eaff", "#241d9a", "#f75092", "#9f50d3"],
        distortion: 0.8,
        swirl: 0.1,
        grainMixer: 0,
        grainOverlay: 0,
        speed: 1,
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#f75092' },
        button: {
          primary: { bg: '#f75092', text: '#ffffff', hover: '#e6407f' },
          secondary: darkThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'mesh-2',
      name: 'Monochrome',
      props: {
        colors: ["#ffffff", "#000000"],
        distortion: 1,
        swirl: 0.2,
        grainMixer: 0,
        grainOverlay: 0,
        speed: 1,
        rotation: 90,
      },
      theme: {
        ...darkThemeBase,
        background: { overlay: 'rgba(0, 0, 0, 0.5)', overlayStrength: 0.5 },
      },
    },
    {
      id: 'mesh-4',
      name: 'Deep Purple Alt',
      props: {
        colors: ["#aaa7d7", "#3b2a8d"],
        distortion: 1,
        swirl: 1,
        grainMixer: 0,
        grainOverlay: 0,
        speed: 0.6,
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#aaa7d7' },
        button: {
          primary: { bg: '#aaa7d7', text: '#1a1a2e', hover: '#9b98c8' },
          secondary: darkThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'mesh-5',
      name: 'Deep Pink',
      props: {
        colors: ["#8B008B", "#C71585", "#800080", "#DC143C"],
        distortion: 0.9,
        swirl: 0.5,
        grainMixer: 0,
        grainOverlay: 0,
        speed: 0.7,
      },
      theme: {
        // Fondo rosado oscuro → Texto claro (aplicando teoría del color)
        background: {
          overlay: 'rgba(0, 0, 0, 0.4)', // Overlay oscuro para mejor contraste
          overlayStrength: 0.4,
        },
        text: {
          primary: '#FFFFFF',       // Blanco - máximo contraste sobre rosa oscuro
          secondary: '#E4E4E7',     // Gris muy claro - buen contraste
          muted: '#A1A1AA',         // Gris claro - para texto secundario
          accent: '#FFB6C1',        // Rosa claro para acentos
        },
        panel: {
          bg: 'rgba(0, 0, 0, 0.5)',
          border: 'rgba(255, 255, 255, 0.15)',
          shadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        },
        button: {
          primary: {
            bg: '#FFB6C1',          // Rosa claro
            text: '#0A0A0A',        // Negro sobre botón rosa claro
            hover: '#FFA0B0',
          },
          secondary: {
            bg: 'rgba(255, 255, 255, 0.1)',
            text: '#FFFFFF',       // Blanco sobre fondo oscuro
            hover: 'rgba(255, 255, 255, 0.2)',
          },
        },
        fonts: {
          heading: "'Inter', system-ui, sans-serif",
          body: "'Inter', system-ui, sans-serif",
          mono: "'SF Mono', Monaco, Consolas, monospace",
        },
        radius: {
          sm: '2px',
          md: '4px',
          lg: '8px',
        },
        mood: 'dark',
      },
    },
  ],
  renderShader: ({ width, height, presetProps }) => (
    <MeshGradient width={width} height={height} {...presetProps} />
  ),
};

// ============================================
// IMAGE DITHERING STYLE (4 presets)
// ============================================
const imageDitheringStyle: ShaderStyle = {
  id: 'image-dithering',
  name: 'ImageDithering',
  presets: [
    {
      id: 'dither-1',
      name: 'Cyber',
      props: {
        image: "https://paper.design/flowers.webp",
        colorBack: "#000c38",
        colorFront: "#94ffaf",
        colorHighlight: "#eaff94",
        originalColors: false,
        type: "8x8",
        size: 2,
        colorSteps: 2,
        fit: "cover",
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#94ffaf' },
        button: {
          primary: { bg: '#94ffaf', text: '#000c38', hover: '#7de89a' },
          secondary: darkThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'dither-2',
      name: 'Sepia',
      props: {
        image: "https://paper.design/flowers.webp",
        colorBack: "#000000",
        colorFront: "#a2997c",
        colorHighlight: "#ededed",
        originalColors: false,
        type: "random",
        size: 1,
        colorSteps: 1,
        fit: "cover",
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#a2997c' },
      },
    },
    {
      id: 'dither-3',
      name: 'Retro',
      props: {
        image: "https://paper.design/flowers.webp",
        colorBack: "#5452ff",
        colorFront: "#ededed",
        colorHighlight: "#ededed",
        originalColors: true,
        type: "2x2",
        size: 3,
        colorSteps: 1,
        fit: "cover",
      },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0A0A0A',      // Negro para contraste en fondo claro
          secondary: '#27272A',    // Gris oscuro
          muted: '#52525B',        // Gris medio
          accent: '#5452ff',       // Azul para acentos
        },
        panel: {
          bg: 'rgba(255, 255, 255, 0.7)',
          border: 'rgba(0, 0, 0, 0.1)',
          shadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        },
        button: {
          primary: {
            bg: '#5452ff',
            text: '#ffffff',
            hover: '#4341cc',
          },
          secondary: {
            bg: 'rgba(0, 0, 0, 0.05)',
            text: '#0A0A0A',
            hover: 'rgba(0, 0, 0, 0.1)',
          },
        },
        mood: 'light',
      },
    },
    {
      id: 'dither-4',
      name: 'Classic BW',
      props: {
        image: "https://paper.design/flowers.webp",
        colorBack: "#000000",
        colorFront: "#ffffff",
        colorHighlight: "#ffffff",
        originalColors: true,
        type: "8x8",
        size: 2,
        colorSteps: 5,
        fit: "cover",
      },
      theme: darkThemeBase,
    },
  ],
  renderShader: ({ width, height, presetProps }) => (
    <ImageDithering width={width} height={height} {...presetProps} />
  ),
};

// ============================================
// DOT ORBIT STYLE (3 presets)
// ============================================
const dotOrbitStyle: ShaderStyle = {
  id: 'dot-orbit',
  name: 'DotOrbit',
  presets: [
    {
      id: 'orbit-1',
      name: 'Fire',
      props: {
        colors: ["#ffc96b", "#ff6200", "#ff2f00", "#421100", "#1a0000"],
        colorBack: "#000000",
        stepsPerColor: 4,
        size: 1,
        sizeRange: 0,
        spreading: 1,
        speed: 1.5,
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#ff6200' },
        button: {
          primary: { bg: '#ff6200', text: '#ffffff', hover: '#e65800' },
          secondary: darkThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'orbit-2',
      name: 'Cosmic',
      props: {
        colors: ["#ffffff", "#006aff", "#fff675"],
        colorBack: "#000000",
        stepsPerColor: 4,
        size: 0.3,
        sizeRange: 0.2,
        spreading: 1,
        speed: 0.1,
        scale: 0.4,
      },
      theme: {
        ...darkThemeBase,
        text: { ...darkThemeBase.text, accent: '#006aff' },
        button: {
          primary: { bg: '#006aff', text: '#ffffff', hover: '#0055cc' },
          secondary: darkThemeBase.button.secondary,
        },
      },
    },
    {
      id: 'orbit-3',
      name: 'Warning',
      props: {
        colors: ["#000000"],
        colorBack: "#ffe600",
        stepsPerColor: 2,
        size: 0.65,
        sizeRange: 0,
        spreading: 0.3,
        speed: 5,
        scale: 0.5,
      },
      theme: {
        ...lightThemeBase,
        text: {
          primary: '#0a0a0a',
          secondary: '#1a1a1a',
          muted: '#3a3a3a',
          accent: '#0a0a0a',
        },
        button: {
          primary: { bg: '#0a0a0a', text: '#ffe600', hover: '#1a1a1a' },
          secondary: { bg: 'rgba(0, 0, 0, 0.1)', text: '#0a0a0a', hover: 'rgba(0, 0, 0, 0.2)' },
        },
        background: { overlay: 'rgba(255, 230, 0, 0.2)', overlayStrength: 0.2 },
      },
    },
  ],
  renderShader: ({ width, height, presetProps }) => (
    <DotOrbit width={width} height={height} {...presetProps} />
  ),
};

// ============================================
// STYLES REGISTRY (ordered for rotation)
// ============================================
export const stylesRegistry: ShaderStyle[] = [
  geistPixelStyle,
  topologyStyle,
  meshGradientStyle,
];

// Helper to get style by ID
export const getStyleById = (id: string): ShaderStyle | undefined =>
  stylesRegistry.find(s => s.id === id);

// Helper to get preset by IDs
export const getPresetById = (styleId: string, presetId: string): ShaderPreset | undefined => {
  const style = getStyleById(styleId);
  return style?.presets.find(p => p.id === presetId);
};

// Default theme (fallback)
export const defaultTheme = geistPixelTheme;
